import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/** Tactile press feedback shared by every non-link variant. */
const PRESS = "active:translate-y-px"

/**
 * Casing policy: call-to-action variants (default, destructive, outline, secondary) are uppercase;
 * ghost and link keep the author's sentence/title case.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-[0.8125rem] font-semibold tracking-wide whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // shadow-sm (outer) and the inset highlight compose through separate Tailwind shadow layers.
        default: cn(
          "bg-primary text-primary-foreground uppercase shadow-sm inset-shadow-[0_1px_0_rgb(255_255_255/0.15)] hover:bg-primary/90 active:shadow-none",
          PRESS
        ),
        destructive: cn(
          "bg-destructive text-white uppercase shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/20 active:shadow-none dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
          PRESS
        ),
        outline: cn(
          "border bg-background uppercase shadow-xs hover:bg-accent hover:text-accent-foreground active:shadow-none dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
          PRESS
        ),
        secondary: cn(
          "bg-secondary text-secondary-foreground uppercase hover:bg-secondary/80",
          PRESS
        ),
        ghost: cn(
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
          PRESS
        ),
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        compact:
          "h-7 gap-1.5 rounded-md px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 text-sm has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-compact": "size-7 rounded-md [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants, type ButtonProps }
